import React, { useEffect, useState } from 'react';
import { LayoutTemplate, Save, Plus, Trash2, RefreshCw, MoveUp, MoveDown } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select } from '@/components/admin/shared';

const LAYOUT_OPTS = [
  { value: 'single_row',   label: 'Single row' },
  { value: 'two_column',   label: 'Two column' },
  { value: 'three_column', label: 'Three column' },
  { value: 'four_column',  label: 'Four column' },
];
const BG_OPTS = [
  { value: 'default',     label: 'Default (ink translucent)' },
  { value: 'ink',         label: 'Solid ink' },
  { value: 'brand',       label: 'Brand accent' },
  { value: 'transparent', label: 'Transparent' },
];
const KIND_OPTS = [
  { value: 'internal', label: 'Internal link' },
  { value: 'external', label: 'External link' },
  { value: 'legal',    label: 'Legal (auto: legal:<slug>)' },
  { value: 'social',   label: 'Social link' },
  { value: 'custom',   label: 'Custom / mailto:' },
];
const SECTION_OPTS = [
  { value: 'primary',   label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'legal',     label: 'Legal row' },
  { value: 'social',    label: 'Social row' },
];

export default function FooterManager() {
  const [form, setForm] = useState(null);
  const [legalDocs, setLegalDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const [{ data }, { data: docs }] = await Promise.all([
        api.get('/admin/footer-config'),
        api.get('/admin/legal-documents').catch(() => ({ data: [] })),
      ]);
      setForm({ ...data, links: (data.links || []).map((l, i) => ({ ...l, order: l.order ?? (i + 1) * 10 })) });
      setLegalDocs(docs || []);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to load footer'); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const setLink = (i, k, v) => setForm((f) => {
    const arr = [...(f.links || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...f, links: arr };
  });
  const addLink = () => setForm((f) => ({
    ...f,
    links: [...(f.links || []), { label: 'New link', href: '/', kind: 'internal', section: 'primary', enabled: true, order: (f.links?.length || 0 + 1) * 10 + 10 }],
  }));
  const removeLink = (i) => setForm((f) => ({ ...f, links: (f.links || []).filter((_, idx) => idx !== i) }));
  const move = (i, dir) => setForm((f) => {
    const arr = [...(f.links || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, links: arr.map((l, idx) => ({ ...l, order: (idx + 1) * 10 })) };
  });

  const addLegalPreset = () => {
    if (!legalDocs.length) { alert('No legal documents exist yet. Create one first.'); return; }
    setForm((f) => {
      const existing = new Set((f.links || []).map((l) => l.href));
      const additions = legalDocs
        .filter((d) => d.publish_status === 'published' && !existing.has(`legal:${d.slug}`))
        .map((d, idx) => ({ label: d.title, href: `legal:${d.slug}`, kind: 'legal', section: 'legal', enabled: true, order: 100 + idx * 10 }));
      return { ...f, links: [...(f.links || []), ...additions] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.updated_at;
      const { data } = await api.put('/admin/footer-config', payload);
      setForm({ ...data, links: data.links || [] });
      setMsg('Footer configuration saved.');
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">Loading footer…</div>;

  return (
    <div className="space-y-6" data-testid="footer-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutTemplate size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Global Footer</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><RefreshCw size={14} /> Reload</button>
          <button data-testid="footer-save" onClick={save} disabled={saving} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {msg && <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)]">{msg}</div>}

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">Presentation</h4>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Layout" value={form.layout} onChange={set('layout')} options={LAYOUT_OPTS} />
          <Select label="Background style" value={form.background_style} onChange={set('background_style')} options={BG_OPTS} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Footer enabled site-wide" value={form.enabled} onChange={set('enabled')} />
          <Toggle label="Show newsletter box" value={form.show_newsletter} onChange={set('show_newsletter')} />
          <Toggle label="Show social icons" value={form.show_social} onChange={set('show_social')} />
          <Toggle label="Show copyright line" value={form.show_copyright} onChange={set('show_copyright')} />
        </div>
        <TextField label="Copyright override (leave blank to auto-generate from Company Info)" value={form.copyright_override} onChange={set('copyright_override')} placeholder="© 2026–{current_year} Life on Planet G. All Rights Reserved." />
      </section>

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="pg-label text-[10px]">Footer Links</h4>
          <div className="flex items-center gap-2">
            <button data-testid="footer-add-legal" onClick={addLegalPreset} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[11px]">Add published legal docs</button>
            <button data-testid="footer-add-link" onClick={addLink} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[11px] flex items-center gap-1"><Plus size={12} /> Add link</button>
          </div>
        </div>
        {(form.links || []).length === 0 && (
          <p className="text-[11px] font-mono text-[var(--pg-ink-faint)]">No links yet. Add internal, external, legal, or social links.</p>
        )}
        {(form.links || []).map((l, i) => (
          <div key={l.id || i} className="pg-input rounded-[var(--pg-radius-sm)] p-3 grid grid-cols-12 gap-2 items-center">
            <div className="col-span-3"><TextField label="Label" value={l.label} onChange={(v) => setLink(i, 'label', v)} /></div>
            <div className="col-span-3"><TextField label="Href (or legal:<slug>)" value={l.href} onChange={(v) => setLink(i, 'href', v)} /></div>
            <div className="col-span-2"><Select label="Kind" value={l.kind || 'custom'} onChange={(v) => setLink(i, 'kind', v)} options={KIND_OPTS} /></div>
            <div className="col-span-2"><Select label="Section" value={l.section || 'primary'} onChange={(v) => setLink(i, 'section', v)} options={SECTION_OPTS} /></div>
            <div className="col-span-1"><Toggle label="On" value={l.enabled !== false} onChange={(v) => setLink(i, 'enabled', v)} /></div>
            <div className="col-span-1 flex justify-end gap-0.5">
              <button onClick={() => move(i, -1)} className="p-1 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]" aria-label="up"><MoveUp size={14} /></button>
              <button onClick={() => move(i, +1)} className="p-1 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]" aria-label="down"><MoveDown size={14} /></button>
              <button onClick={() => removeLink(i)} className="p-1 text-[var(--pg-ink-muted)] hover:text-[var(--pg-neon-pink)]" aria-label="remove"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </section>

      <div className="flex justify-end">
        <button data-testid="footer-save-bottom" onClick={save} disabled={saving} className="pg-btn-primary px-4 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
          <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
